<template>
  <FormControl :title="title" :sub-title="subTitle">
    <component
      :is="showBankButton && !review ? WordBank : 'div'"
      :side-title="getLocalizedText('word_bank')"
      :class="{ 'bg-white p-5': review }">
      <template #default>
        <div class="text-container">
          <TiptapText
            :id="id"
            v-model="cleanText"
            :editable="false"
            :locale="locale"
            :extensions="extensions"></TiptapText>
        </div>
      </template>
      <template #side>
        <BankBanner v-model="optionsRef" group-name="fill_in_blanks_answers" />
      </template>
    </component>
  </FormControl>
</template>

<script setup lang="ts">
import BankBanner from '@/components/BankBanner/BankBanner.vue';
import { watch, computed, ref, watchEffect, provide } from 'vue';
import { useVModel } from '@vueuse/core';
import FormControl from '@/components/utils/FormControl/FormControl.vue';
import type { IBlanksModel } from '@/types/interfaces';
import { uniq } from 'lodash';
import WordBank from '@/components/WordBank/WordBank.vue';

import TiptapText from '@amit/tiptap/src/TiptapText.vue';
import { shuffle as shuffleArray } from 'lodash';
import { useReveal } from '@/apps/slideshow/useReveal';
interface FillBlanksProps {
  id: string;
  title?: string;
  subTitle?: string;
  text: string;
  format: string;
  options?: Array<any>;
  blanks?: IBlanksModel[];
  modelValue?: Record<string, string[]>;
  locale?: string;
  review?: boolean;
}

const props = withDefaults(defineProps<FillBlanksProps>(), {
  id: '',
  title: '',
  subTitle: '',
  options: () => [],
  blanks: () => [],
  locale: 'he',
  modelValue: () => ({}),
  review: false,
});

const { getLocalizedText } = useReveal();

const extensions = {
  placeholder: { placeholder: '' },
  blank: false,
  keyboard: false,
  blank_view: {
    locale: props.locale,
  },
};

const emit = defineEmits(['update:modelValue']);
const model = useVModel(props, 'modelValue', emit);

const showBankButton = computed(() => props.format === 'dnd');

const optionsRef = ref();
const cleanText = ref();

watchEffect(() => {
  const allOptions = shuffleArray([
    ...props.blanks.flatMap(blank => [
      ...blank.matches,
      ...(blank?.fillers ?? []),
    ]),
    ...props.options,
  ]);
  const selectedOptions = Object.values(model.value).flat();

  selectedOptions.forEach(option => {
    const index = allOptions.indexOf(option);
    if (index !== -1) {
      allOptions.splice(index, 1);
    }
  });

  optionsRef.value = allOptions;
});

const removeElement = (index, wordId) => {
  const element = model.value[wordId].splice(index, 1)[0];
  if (element) {
    optionsRef.value.push(element);
  }
};

watch(
  () => props.modelValue,
  () => {
    props.blanks.forEach(blank => {
      if (!model.value[blank.id]) {
        model.value[blank.id] = [];
      }
    });
    cleanText.value = addModelAttributeToBlanks(props.text);
  },
  { deep: true, immediate: true },
);

const selectOptions = ref([]);
watch(
  () => props.blanks,
  () => {
    props.blanks.forEach(blank => {
      selectOptions.value[blank.id] = shuffleArray(
        uniq([
          ...(blank?.matches ?? []),
          ...(blank?.fillers ?? []),
          ...props.options,
        ]),
      );
    });
  },
  { immediate: true, deep: true },
);

function addModelAttributeToBlanks(html) {
  return html.replace(
    /<blank id="(.*?)"(.*?)class="blank"/g,
    (_, id, otherAttrs) => {
      return `<blank id="${id}" model='${JSON.stringify(model.value[id])}' ${otherAttrs} class="blank"`;
    },
  );
}

provide('format', props.format);
provide('options', selectOptions);
provide('removeElement', removeElement);
provide('model', model);
</script>

<style scoped>
:deep(p) {
  line-height: 36px;
  background-color: transparent !important;
}
</style>
